import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useTenant } from '../../contexts/TenantContext'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { supabase } from '../../lib/supabase'
import { Tenant } from '../../lib/types'
import { MainLayout } from '../../components/layouts/MainLayout'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { Building2, Plus, Users, BookOpen, Save, X } from 'lucide-react'

export function TenantsManagePage() {
  useDocumentTitle('テナント管理')
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isPlatformAdmin } = useTenant()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    representative_name: '',
    admin_email: '',
    admin_password: '',
    admin_full_name: '',
  })

  useEffect(() => {
    if (!isPlatformAdmin) {
      navigate('/mypage')
      return
    }

    if (user) {
      fetchTenants()
    }
  }, [user, isPlatformAdmin])

  const fetchTenants = async () => {
    try {
      setLoading(true)
      setError(null)

      const { data, error: tenantsError } = await supabase
        .from('tenants')
        .select('*')
        .order('created_at', { ascending: false })

      if (tenantsError) throw tenantsError

      setTenants(data || [])
    } catch (err: any) {
      console.error('Error fetching tenants:', err)
      setError(err.message || 'テナントの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      setSaving(true)
      setError(null)

      // 1. テナントを作成
      const { data: tenantData, error: tenantError } = await supabase
        .from('tenants')
        .insert([
          {
            name: formData.name,
            representative_name: formData.representative_name,
            status: 'active',
          },
        ])
        .select()
        .single()

      if (tenantError) throw tenantError

      // 2. 管理者ユーザーを作成
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.admin_email,
        password: formData.admin_password,
        options: {
          data: {
            full_name: formData.admin_full_name,
          },
        },
      })

      if (authError) throw authError

      if (authData.user) {
        // 3. memberships に tenant_admin として追加
        await supabase.from('memberships').insert([
          {
            user_id: authData.user.id,
            tenant_id: tenantData.tenant_id,
            role: 'tenant_admin',
            status: 'active',
          },
        ])
      }

      // フォームリセット
      setFormData({
        name: '',
        representative_name: '',
        admin_email: '',
        admin_password: '',
        admin_full_name: '',
      })
      setShowForm(false)
      fetchTenants()
    } catch (err: any) {
      console.error('Error creating tenant:', err)
      setError(err.message || 'テナントの作成に失敗しました')
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <MainLayout>
        <Loading message="テナントを読み込み中..." />
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">テナント管理</h1>
            <p className="mt-2 text-gray-600">認証団体の作成・管理</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition font-medium"
          >
            <Plus className="h-5 w-5" />
            新規テナント作成
          </button>
        </div>

        {error && (
          <div className="mb-6">
            <ErrorMessage message={error} />
          </div>
        )}

        {/* テナント作成フォーム */}
        {showForm && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">新規テナント作成</h2>
              <button
                onClick={() => setShowForm(false)}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded transition"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleCreateTenant} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    団体名 <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="例: 日本認定協会"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    代表者名 <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.representative_name}
                    onChange={(e) =>
                      setFormData({ ...formData, representative_name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="例: 山田太郎"
                  />
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">管理者アカウント</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      管理者氏名 <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.admin_full_name}
                      onChange={(e) =>
                        setFormData({ ...formData, admin_full_name: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      管理者メールアドレス <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.admin_email}
                      onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      初期パスワード <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="password"
                      required
                      minLength={8}
                      value={formData.admin_password}
                      onChange={(e) =>
                        setFormData({ ...formData, admin_password: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="8文字以上"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition"
                >
                  <Save className="h-5 w-5" />
                  {saving ? '作成中...' : '作成する'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  disabled={saving}
                  className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition disabled:opacity-50"
                >
                  キャンセル
                </button>
              </div>
            </form>
          </div>
        )}

        {/* テナント一覧 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tenants.map((tenant) => (
            <div key={tenant.tenant_id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Building2 className="h-6 w-6 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">{tenant.name}</h3>
                </div>
                <span
                  className={`px-2 py-1 text-xs rounded ${
                    tenant.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {tenant.status === 'active' ? '有効' : '停止'}
                </span>
              </div>

              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span>代表者: {tenant.representative_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  <span>作成日: {new Date(tenant.created_at).toLocaleDateString('ja-JP')}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </MainLayout>
  )
}
